import { t } from '@lingui/core/macro';
import type { ComponentType } from 'react';
import { useMemo } from 'react';

import type { OptionType } from '@/api/common';
import {
  NumberInput,
  NumberInputGroup,
  NumberInputInput,
  NumberInputTrigger,
} from '@/components/Form';
import {
  AudienceMultiSelect,
  CampaignNameMultiSelect,
  ChannelMultiSelect,
  CompanyNameMultiSelect,
  FreeFormSelect,
  IndustryMultiSelect,
  IntentProductCategoryMultiSelect,
  IntentProviderSelect,
  KeywordGroupNameMultiSelect,
  KeywordNameMultiSelect,
  PixelNameMultiSelect,
  RegionMultiSelect,
  RevRangeMultiSelect,
  SignalTypeMultiSelect,
  VendorMultiSelect,
} from '@/features/inputs';

import type { AttributeValueType, RuleEntityAttributeType } from './types';

type Props = {
  attribute: RuleEntityAttributeType;
  value?: AttributeValueType | null;
  'aria-invalid'?: boolean;
  disabled?: boolean;
  onChange?: (value: AttributeValueType | null) => void;
};

// Static map of attribute.id -> component (declared outside render)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const VALUE_COMPONENTS: Partial<Record<RuleEntityAttributeType['id'], ComponentType<any>>> = {
  pixel: PixelNameMultiSelect,
  name: CompanyNameMultiSelect,
  campaign: CampaignNameMultiSelect,
  ad: KeywordNameMultiSelect,
  adGroup: KeywordGroupNameMultiSelect,
  productCategory: IntentProductCategoryMultiSelect,
  industry: IndustryMultiSelect,
  revRange: RevRangeMultiSelect,
  region: RegionMultiSelect,
  audience: AudienceMultiSelect,
  vendor: VendorMultiSelect,
  channel: ChannelMultiSelect,
  signalType: SignalTypeMultiSelect,
  provider: IntentProviderSelect,
};

const RuleBuilderValueInput = ({
  attribute,
  value,
  onChange,
  'aria-invalid': ariaInvalid,
  ...props
}: Props) => {
  const ValueComponent = useMemo(() => VALUE_COMPONENTS[attribute.id], [attribute.id]);

  if (attribute.display === 'autocomplete') {
    if (!ValueComponent) {
      throw new Error(`No value component found for attribute ${attribute.id}`);
    }

    return (
      <ValueComponent
        {...props}
        aria-invalid={ariaInvalid || undefined}
        value={value}
        onChange={(val: string[] | null) => onChange?.(val)}
      />
    );
  }

  if (attribute.display === 'picklist') {
    if (!ValueComponent) {
      throw new Error(`No value component found for attribute ${attribute.id}`);
    }

    return (
      <ValueComponent
        {...props}
        aria-invalid={ariaInvalid || undefined}
        value={value?.map((v) => ({ id: v as string }))}
        onChange={(val: OptionType[]) => onChange?.(val.map((v) => v.id))}
      />
    );
  }

  if (attribute.display === 'picker') {
    if (!ValueComponent) {
      throw new Error(`No value component found for attribute ${attribute.id}`);
    }

    return (
      <ValueComponent
        {...props}
        aria-invalid={ariaInvalid || undefined}
        value={value?.[0] ? { id: value[0] as string } : undefined}
        onChange={(val: OptionType | OptionType[]) => {
          const selectedValue = Array.isArray(val) ? val[0]?.id : val?.id;
          onChange?.(selectedValue ? [selectedValue] : []);
        }}
      />
    );
  }

  if (attribute.display === 'text') {
    return (
      <FreeFormSelect
        {...props}
        aria-invalid={ariaInvalid || undefined}
        placeholder={t`Type here...`}
        value={value as string[] | undefined}
        onChange={(val: string[] | null) => onChange?.(val)}
      />
    );
  }

  if (attribute.display === 'number') {
    const numberValue = typeof value?.[0] === 'number' ? value[0] : undefined;
    return (
      <NumberInput
        {...props}
        style={{ width: 120 }}
        min={0}
        max={99999}
        value={numberValue}
        aria-invalid={ariaInvalid || undefined}
        format={{ useGrouping: true, maximumFractionDigits: 0 }}
        onValueChange={(nextValue) => onChange?.(nextValue == null ? [] : [nextValue])}
      >
        <NumberInputGroup>
          <NumberInputInput data-testid="number-attribute-input" />
          <NumberInputTrigger />
        </NumberInputGroup>
      </NumberInput>
    );
  }

  throw new Error(`No display type found for attribute ${attribute.id}`);
};

export default RuleBuilderValueInput;
